import React, {Component, Fragment} from 'react';
import {Product, ProductSKU} from 'services/entities/products';
import {DraftBooking} from 'services/entities/booking';
import {AddIcon, FurnitureIcon, NextChevronIcon, PreviousChevronIcon, RemoveIcon} from 'components/Icon';
import './FurnitureCategory.scss';
import {Button, MenuItem} from '@material-ui/core';
import {getDraftBookingProductOption, getDraftBookingProductQuantity} from 'services/bookings/drafts';
import {CurrencyAmount} from 'util/currency';
import {applyCurrencyTaxes} from 'util/pricing';
import {handleError} from 'util/errors';
import ExpandableItem from '../components/ExpandableItem';
import SelectField from 'components/forms/SelectField';
import Carousel from 'components/Carousel';

interface FurnitureCategoryProps {
  booking: DraftBooking|null;
  products: Product[]|null;
  onProductQuantityChanged:(sku: string, quantity: number)=>void;
  onProductOptionValueChanged:(sku: string, option: string, value: string|null)=>void;
}

interface FurnitureCategoryState {
  productPrices: {[index:string]:CurrencyAmount};
}

export default class FurnitureCategory extends Component<FurnitureCategoryProps, FurnitureCategoryState> {
  state = {
    productPrices: {}
  };

  componentDidMount(): void {
    if(this.props.products) {
      for(const pr of this.props.products) {
        this.loadProductPrice(pr);
      }
    }
  }

  componentDidUpdate(prevProps: Readonly<FurnitureCategoryProps>, prevState: Readonly<FurnitureCategoryState>, snapshot?: any): void {
    if(prevProps.products !== this.props.products && this.props.products) {
      for(const pr of this.props.products) {
        this.loadProductPrice(pr);
      }
    }
  }

  loadProductPrice(product: Product) {
    const price = product.prices.find(pr => pr.currency && pr.currency.isDefault);
    if(!price) {
      return;
    }
    applyCurrencyTaxes(price).then(
      totalPrice => {
        const productPrices: {[index:string]:CurrencyAmount} = this.state.productPrices;
        productPrices[product.sku] = totalPrice;
        this.setState({'productPrices': productPrices});
      },
      ex => handleError('Failed loading total price', ex)
    );
  }

  onDecreaseQuantityClick = (product: Product) => {
    const quantity = this.props.booking ? getDraftBookingProductQuantity(this.props.booking, product.sku) : 0;
    if(quantity > 0) {
      this.props.onProductQuantityChanged(product.sku, quantity - 1);
    }
  };

  onIncreaseQuantityClick = (product: Product) => {
    const quantity = this.props.booking ? getDraftBookingProductQuantity(this.props.booking, product.sku) : 0;
    if(product.maxQuantity <= 0 || quantity < product.maxQuantity) {
      this.props.onProductQuantityChanged(product.sku, quantity + 1);
    }
  };

  onProductOptionValueChanged = (product: Product, option: string, val: string) => {
    this.props.onProductOptionValueChanged(product.sku, option, val);
  };

  renderProductDetails(product: Product, name?: string) {
    const booking = this.props.booking;
    const quantity = booking ? getDraftBookingProductQuantity(booking, product.sku) : 0;
    let description = product.description;
    for(const op of product.options) {
      const valueCode = booking ? getDraftBookingProductOption(booking, product.sku, op.code) : null;
      let value = valueCode ? op.values.find(v => v.code === valueCode) : null;
      if(!value) {
        value = op.values[0];
      }
      if(value.description) {
        description = value.description;
      }
    }
    return <div className="details">
      <h4>{ name || product.name }</h4>
      { description && <div className="description" dangerouslySetInnerHTML={{__html: description}} /> }
      <div className="quantity-available">
        <span className="label">Available</span>
        <span className="value">{ product.maxQuantity }</span>
      </div>
      {product.options && product.options.length > 0 &&
        <div className="options">
          { product.options.map(op => {
            const currentValue = booking ? getDraftBookingProductOption(booking, product.sku, op.code) : null;
            return <Fragment key={op.code}>
              <label>{ op.name }</label>
              <SelectField placeholder="Select configuration" value={currentValue}
                           onChange={val => this.onProductOptionValueChanged(product, op.code, val)}>
                {op.values.map(v => <MenuItem key={v.code} value={v.code}>{v.name}</MenuItem>)}
              </SelectField>
            </Fragment>;
          }) }
        </div>
      }
      <div className="quantity">
        <label>Select quantity</label>
        <Button onClick={() => this.onDecreaseQuantityClick(product)}><RemoveIcon/></Button>
        <span className="value">{ quantity }</span>
        <Button onClick={() => this.onIncreaseQuantityClick(product)}><AddIcon/></Button>
      </div>
    </div>;
  }

  renderProductPrice(price: CurrencyAmount|undefined) {
    return <div className="price">
      { price && price.amount > 0 ? `${price.format("$0.00")} inc VAT p/h` : 'Free' }
    </div>;
  }

  getCurrentProductOptionValueIndex(product: Product, optionCode: string): number {
    if(!this.props.booking) {
      console.warn("Booking not set");
      return 0;
    }
    const option = product.options.find(op => op.code === optionCode);
    if(!option) {
      console.warn("Product option not found: " + optionCode);
      return 0;
    }
    const currentValueCode = getDraftBookingProductOption(this.props.booking, product.sku, optionCode);
    const currentValue = currentValueCode ? option.values.find(v => v.code === currentValueCode) : null;
    return currentValue ? option.values.indexOf(currentValue) : 0;
  }

  moveProductOptionValueIndex(product: Product, optionCode: string, change: number) {
    const option = product.options.find(op => op.code === optionCode);
    if(!option) {
      console.warn("Product option not found: " + optionCode);
      return;
    }
    const currentIndex = this.getCurrentProductOptionValueIndex(product, optionCode);
    let newIndex = (currentIndex + change) % option.values.length;
    if(newIndex < 0) {
      newIndex = option.values.length - 1;
    }
    const newValueCode = option.values[newIndex].code;
    this.props.onProductOptionValueChanged(product.sku, optionCode, newValueCode);
  }

  onSelectPreviousProductOptionValueClick = (product: Product, optionCode: string) => {
    this.moveProductOptionValueIndex(product, optionCode, -1);
  };

  onSelectNextProductOptionValueClick = (product: Product, optionCode: string) => {
    this.moveProductOptionValueIndex(product, optionCode, 1);
  };

  renderProductOptions(product: Product) {
    if(!product.options || product.options.length === 0) {
      return null;
    }
    return <div className="options">
      { product.options.map(op => {
        const valueIndex = this.getCurrentProductOptionValueIndex(product, op.code);
        return <Fragment key={op.code}>
          <Carousel key={op.code} items={op.values.map(v => v.image ? <img src={v.image} alt={v.name} /> : v.name)} currentItem={valueIndex} />
          <button className="previous" onClick={() => this.onSelectPreviousProductOptionValueClick(product, op.code)}><PreviousChevronIcon/></button>
          <span className="value-name">Config. {valueIndex + 1} / {op.values.length }</span>
          <button className="next" onClick={() => this.onSelectNextProductOptionValueClick(product, op.code)}><NextChevronIcon/></button>
        </Fragment>;
      } ) }
    </div>;
  }

  render() {
    const products: Product[]|null = this.props.products;
    if(!products || products.length === 0) {
      return null;
    }
    const productPrices: {[index:string]: CurrencyAmount} = this.state.productPrices;
    const chairsProduct = products.find(pr => pr.sku === ProductSKU.Chairs);
    const tablesProduct = products.find(pr => pr.sku === ProductSKU.Tables);
    const tablesChairsPrice = chairsProduct ? productPrices[chairsProduct.sku] : undefined;
    return <ExpandableItem icon={<FurnitureIcon/>} title={'Furniture'} className={'furniture'}
                     description={<p>Select from our range of furniture options to book for your space.</p>}>
      <div className="product tables-chairs">
        { chairsProduct && chairsProduct.image && <img src={chairsProduct.image} alt="Tables & Chairs" className="hero" /> }
        <div>
          <h3>Tables &amp; Chairs</h3>
          { this.renderProductPrice(tablesChairsPrice) }
          { chairsProduct && this.renderProductDetails(chairsProduct) }
          { tablesProduct && this.renderProductDetails(tablesProduct) }
        </div>
      </div>
      { products.filter(pr => pr.sku !== ProductSKU.Chairs && pr.sku !== ProductSKU.Tables).map(pr => {
        return <div key={pr.sku} className="product">
          { this.renderProductOptions(pr) }
          <div className="info">
            <h3>{ pr.name }</h3>
            { this.renderProductPrice(productPrices[pr.sku]) }
            { this.renderProductDetails(pr) }
          </div>
        </div>;
      })}
    </ExpandableItem>;
  }
}